// libraries
import type { ITradeInState, IStateTreeWithOffer } from '@makemydeal/dr-activities-common';

// consts/enums
import { TradeInSubaruGtpFlowErrorType } from './tradeInEnums';
import { tradeInMethod } from '../utils/tradeInDecisionLogic';

export interface IConflict {
    lhs: boolean;
    path: string[];
}

export interface ISavedEstimate {
    selected: boolean;
    value: number;
}

export interface ISaved {
    amountOwed: number;
    estimate: ISavedEstimate[];
    location: any; // TODO: Define type
    value?: number;
    vehicle?: any; // TODO: Define type
    vehicleOptions?: any; // TODO: Define type
}

export interface ITradeInModel {
    name: string;
    id: string;
}

export interface ITradeInMake {
    name: string;
    id: string;
}

export interface ITradeInTrim {
    name: string;
    id: string;
    vehicleId: string;
}

export interface ILienholder {
    partnerId: string;
    name: string;
}

export interface ILienholders {
    isLoading: boolean;
    options: ILienholder[];
}

export interface IPayoffQuote {
    isLoading: boolean;
    options: IPayoffQuoteResponse | {};
}

type FinanceMethods = 'Retail' | 'Lease' | 'Ballon';
export type SearchKeyFields = 'ACC' | 'VIN';

export interface ILienHolderOptionsConfig {
    keyId: SearchKeyFields;
    name: string;
    lastNameRequired: boolean;
    lastXOf: boolean;
    ssnLast4Required: boolean;
    minLength?: number;
    maxLength?: number;
    length?: number;
}
export interface ILienholdersOptions {
    isLoading: boolean;
    options: ILienHolderOptionsConfig[];
}

export interface ITradeInSaved {
    amountOwed: number;
    estimate: number[] | {}; // TODO: This doesn't seem right!
    location: any;
    offerDate: string;
    offerExpirationDate: string;
    status: string;
    tivSource: string;
    value: number;
    vehicle: any; // TODO: Define type
    vehicleOptions: any; // TODO: Define type
    zip: string;
    shouldIncludeTradeIn?: boolean;
    ownership?: any; // TODO: Define type
    valueSource?: string;
    valueComments?: string;
}

export interface ITradeInSubaruGtpFlowError {
    errorMessage?: string;
    errorType: TradeInSubaruGtpFlowErrorType;
}

export interface ITradeInComponentState extends ITradeInState {
    isEquipOptionsEstimate1InProgress?: boolean;
    inTradeInSubaruGtpFlow?: boolean;
    isICOTradeAlreadySubmitted: boolean;
    isExternalIcoCompleted: boolean;
    isShopperEnteredTrade?: boolean;
    isSubaruGtpEligible?: boolean;
    shouldIncludeTradeIn?: boolean;
    hasOpenLoan?: boolean;
    selectedLoanBalanceOption?: string;
    lienholders?: ILienholders;
    lienholdersOptions?: ILienholdersOptions;
    tradeInSubaruGtpFlowError?: ITradeInSubaruGtpFlowError;
    toastClosed?: boolean;
    dealChangeMessageHasBeenClosed?: boolean;
    skippedTradeIn?: boolean;
    entryPoint?: string;
    tradeInPayoffQuoteData?: IPayoffQuote;
    pendingIcoTradeAddedPayload?: any;
    pendingIcoTradeCompletedPayload?: any;
    showTradeInRemovalConfirmationModal?: boolean;
    pendingNavigationRoute?: string;
}

export interface ITradeInPayoffQuote {
    amountOwed?: number;
    amountOwedSource?: string;
    financeMethod?: string;
    goodThroughDate?: string;
    lenderName?: string;
}

export interface ITradeInActivityState extends IStateTreeWithOffer {}

export type StepperRoute = {
    name: string;
    route: string;
    analyticsId: string;
    completed?: boolean;
};

export type SelectedOption = {
    selectedOptionValue: string;
    selectedOptionIndex: number;
};

export type UniqueOption = {
    hasUniqueOption: boolean;
    uniqueOptionValue: tradeInMethod;
};

export type TradeInAssertionSelectors = { [selector: string]: boolean };

export type TradeInStepName = 'Vehicle Info' | 'Estimate Method' | 'Details' | 'Loan Balance' | 'Summary';

interface Finance {
    financeMethod: FinanceMethods;
    payoffAmount: string;
    goodUntilDate: string;
    maturityDate: string;
    originalTermCount: string;
    remainingTermCount: string;
    nextPaymentDate: string;
    monthlyPaymentAmount: string;
    dollarDaysRatio: string;
    loanAmount?: string;
    loanBalanceAmount?: string;
    residualValueAmount?: string;
    partnerId?: string;
}

interface Customer {
    firstName: string;
    lastName: string;
    account: string;
    coapplicant?: string;
}

interface PayoffQuoteVehicle {
    make: string;
    model: string;
    trim: string;
    vehicleDescription?: string;
    vin: string;
    year: string;
}

interface RawPayoffQuoteResponse {
    payoffMsg?: string;
    footnote?: string;
    vehicleInfo: PayoffQuoteVehicle;
    customer: Customer;
    finance: Finance;
    valuedCustomer: {
        preapprovalReferenceNumber: string;
    };
}

interface TitleDetail {
    titleId: string;
    titleGuid: string;
    titleState: string;
    titleFormat: TitleFormat;
    titleAvailabilityIndicator: TitleAvailbility;
    titleAvailabilityDescription: string;
}

enum TitleFormat {
    ELT = 'ELT',
    PAPER = 'PAPER'
}

enum TitleAvailbility {
    TITLE_IS_RELEASABLE = 'TITLE_IS_RELEASABLE',
    TITLE_IS_NOT_RELEASABLE = 'TITLE_IS_NOT_RELEASABLE',
    TITLE_IS_RELEASABLE_OTHER = 'TITLE_IS_RELEASABLE_OTHER',
    TITLE_STATUS_NOT_AVAILABLE = 'TITLE_STATUS_NOT_AVAILABLE'
}

interface PayoffInstructions {
    hasPayoffInstructions: boolean;
    message?: string;
    instructions?: string;
}

export interface PayoffQuoteResponse extends RawPayoffQuoteResponse {
    finance: RawPayoffQuoteResponse['finance'] & {
        acceleratedTitleShippingCharges: string;
        acceleratedTitleTaxFee: string;
        acceleratedTitleTransactionCharges: string;
        originalPayoffAmount: string;
        originalGoodUntilDate: string;
    };
    titleDetail: TitleDetail;
    payoffInstructions: PayoffInstructions;
}
export interface IPayoffQuoteResponse {
    payoffQuoteId: string;
    payoffQuoteResponse: PayoffQuoteResponse[];
}
